/* V25_APPLICATION_UI_RESEARCH_EXPLANATION_LAYER_V1 */
import React from "react";
import { analysis } from "../../../data/needs";
import { fmt, humanizeToken } from "./needsApplicationModel";

function unique(values = []) {
  return [...new Set((values || []).filter(Boolean))];
}

function shorten(text = "", limit = 285) {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).trim()}…`;
}

function capabilityDeepEntries(capabilityId, rootIds = []) {
  const deep = analysis?.deep || {};
  return rootIds
    .map((rootId) => ({
      rootId,
      entry: deep[`${rootId}|${capabilityId}`] || null,
    }))
    .filter((item) => item.entry);
}

function roleSamples(entries = []) {
  const wanted = [
    ["current_condition", "Current condition"],
    ["participant_need", "Participant need"],
    ["desired_shift", "Desired shift"],
  ];

  const samples = [];

  for (const [role, label] of wanted) {
    let found = null;

    for (const item of entries) {
      const excerpt = (item.entry?.excerpts || []).find(
        (candidate) => candidate.excerpt_role === role
      );
      if (excerpt?.passage) {
        found = {
          rootId: item.rootId,
          role,
          label,
          passage: excerpt.passage,
          provenanceStatus: excerpt.provenance_status,
        };
        break;
      }
    }

    if (found) samples.push(found);
  }

  return samples;
}

function relationVocabulary(entries = []) {
  const labels = [];

  for (const item of entries) {
    for (const relation of item.entry?.relations || []) {
      if (relation?.label) labels.push(relation.label);
    }
  }

  const counts = new Map();
  for (const label of labels) {
    counts.set(label, (counts.get(label) || 0) + 1);
  }

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
}

function DepthCard({ big, label, note }) {
  return (
    <div className="v25a-explain-depth-card">
      <b>{big}</b>
      <span>{label}</span>
      {note ? <small>{note}</small> : null}
    </div>
  );
}

function V24Explanation({
  condition,
  capabilityId,
  profile,
  onOpenResearch,
}) {
  const rootIds = unique([
    ...(profile?.core_roots || []),
    ...(profile?.partial_roots || []),
  ]);

  const entries = capabilityDeepEntries(capabilityId, rootIds);
  const deepRelational = entries.filter(
    ({ entry }) =>
      entry?.status === "atom_proposition_relation_and_source_passage"
  );
  const structureOnly = entries.filter(
    ({ entry }) => entry?.status === "v21_structure_excerpt_only"
  );
  const rootOnly = entries.filter(
    ({ entry }) => entry?.status === "research_root_only"
  );

  const samples = roleSamples(entries);
  const vocabulary = relationVocabulary(deepRelational);

  return (
    <section className="v25a-research-explanation">
      <div className="v25a-explain-heading">
        <div>
          <span className="v25a-eyebrow">
            RESEARCH EXPLANATION · BEFORE THE STUDIES
          </span>
          <h4>Hva forteller forskningslaget oss her?</h4>
        </div>
        <span className="v25a-explain-mode">CAPABILITY PROFILE CONTEXT</span>
      </div>

      <div className="v25a-explain-thesis">
        <span className="v25a-eyebrow">READ THIS FIRST</span>
        <p>
          <b>{condition}</b> er synlig i capability-profilen. På dette
          epistemiske nivået kan vi vise hvordan research-korpuset rundt hele
          kapabiliteten er strukturert, hvilke typer kildeutsnitt som finnes,
          og hvilke relasjonelle spor som er tilgjengelige. Vi kan ikke
          ærlig si at hver studie nedenfor direkte forklarer akkurat denne
          betingelsen.
        </p>
      </div>

      <div className="v25a-explain-depth-grid">
        <DepthCard
          big={rootIds.length}
          label="StudyRoots"
          note="capability-level"
        />
        <DepthCard
          big={deepRelational.length}
          label="deep relational"
          note="atom + relation"
        />
        <DepthCard
          big={structureOnly.length}
          label="structure excerpts"
          note="V21 handoff"
        />
        <DepthCard
          big={rootOnly.length}
          label="root only"
          note="no deeper trace here"
        />
      </div>

      {samples.length ? (
        <div className="v25a-explain-signals">
          <div className="v25a-explain-section-head">
            <span className="v25a-eyebrow">REPRESENTATIVE RESEARCH SIGNALS</span>
            <small>
              capability-level excerpts · not condition-specific causal proof
            </small>
          </div>

          <div className="v25a-signal-list">
            {samples.map((sample) => (
              <article key={`${sample.rootId}-${sample.role}`}>
                <div>
                  <span>{sample.label}</span>
                  <small>{sample.rootId}</small>
                </div>
                <p>{shorten(sample.passage)}</p>
                <small className="v25a-signal-provenance">
                  {sample.provenanceStatus ||
                    "V21 structure excerpt · not an evidence atom"}
                </small>
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {vocabulary.length ? (
        <div className="v25a-explain-vocabulary">
          <span className="v25a-eyebrow">
            RELATIONAL VOCABULARY IN DEEPER CAPABILITY TRACES
          </span>
          <div>
            {vocabulary.map(([label, count]) => (
              <span key={label}>
                {humanizeToken(label)} <b>{count}</b>
              </span>
            ))}
          </div>
          <small>
            Dette beskriver tilgjengelige relasjonstyper i capability-research,
            ikke en affording/constraining-klassifisering av den valgte
            V24-betingelsen.
          </small>
        </div>
      ) : null}

      <div className="v25a-explain-boundary">
        <div>
          <span className="v25a-eyebrow">WHAT WE CAN SAY</span>
          <p>
            Denne betingelsen er del av en forskningsforankret capability-profil,
            og research-korpuset kan inspiseres videre.
          </p>
        </div>
        <div>
          <span className="v25a-eyebrow">WHAT WE CANNOT YET SAY</span>
          <p>
            At betingelsen er en materialisert pathway, inhibitor, enabler eller kausal
            driver.
          </p>
        </div>
      </div>

      <button
        type="button"
        className="v25a-explain-open"
        onClick={onOpenResearch}
      >
        Se studiene bak research-konteksten →
      </button>
    </section>
  );
}

function V25Explanation({
  group,
  detail,
  onOpenResearch,
}) {
  const pathway = detail?.pathway || group?.pathways?.[0] || {};
  const assignment = detail?.assignment || {};
  const projection = detail?.projection || {};
  const atom = detail?.relational_atom || {};
  const source = detail?.source_matter || {};

  const orientation =
    pathway.contribution_orientation ||
    group?.orientation ||
    "unresolved";

  const sourcePhrase =
    atom.source_phrase ||
    projection.source_phrase ||
    "";
  const targetPhrase =
    atom.target_phrase ||
    projection.target_phrase ||
    "";

  return (
    <section className="v25a-research-explanation">
      <div className="v25a-explain-heading">
        <div>
          <span className="v25a-eyebrow">
            RESEARCH EXPLANATION · BEFORE PROVENANCE
          </span>
          <h4>Hvorfor er denne pathwayen analytisk synlig?</h4>
        </div>
        <span className="v25a-explain-mode">WARRANTED PATHWAY</span>
      </div>

      <div className="v25a-explain-thesis v25">
        <span className="v25a-eyebrow">READ THIS FIRST</span>
        <p>
          <b>{group?.label || "Denne pathwayen"}</b> er ikke en studie og ikke
          en score. Det er en warranted configuration pathway som gjør en
          bestemt relasjonell lesning synlig i denne konfigurasjonen. Her
          bidrar den <b>{humanizeToken(orientation)}</b> relativt til
          kapabiliteten.
        </p>
      </div>

      <div className="v25a-explain-depth-grid">
        <DepthCard
          big={group?.rootCount ?? 1}
          label="independent roots"
          note="within visual group"
        />
        <DepthCard
          big={group?.pathwayCount ?? 1}
          label="pathways"
          note="same condition/orientation"
        />
        <DepthCard
          big={fmt(
            pathway.contribution_warrant ?? group?.averageWarrant,
            2
          )}
          label="warrant"
          note="pathway contribution"
        />
        <DepthCard
          big={fmt(assignment.specificity_score, 2)}
          label="specificity"
          note="competitive assignment"
        />
      </div>

      {(sourcePhrase || targetPhrase) ? (
        <div className="v25a-source-near-preview">
          <span className="v25a-eyebrow">SOURCE-NEAR RELATION</span>
          <div>
            <b>{sourcePhrase || "source"}</b>
            <span>→</span>
            <b>{targetPhrase || "target"}</b>
          </div>
          <small>
            Denne relasjonen er fortsatt et analytisk mellomledd. Neste zoom
            viser projection, relational atom, SourceMatter og StudyRoot.
          </small>
        </div>
      ) : null}

      {source?.text ? (
        <div className="v25a-source-preview">
          <span className="v25a-eyebrow">SOURCE MATTER PREVIEW</span>
          <p>{shorten(source.text, 330)}</p>
          <small>SOURCE MATTER · DISTANCE 0</small>
        </div>
      ) : null}

      <div className="v25a-explain-boundary">
        <div>
          <span className="v25a-eyebrow">ANALYTICAL CLAIM</span>
          <p>
            Denne pathwayen har en eksplisitt contribution orientation og
            warrant i den valgte configuration state.
          </p>
        </div>
        <div>
          <span className="v25a-eyebrow">BOUNDARY</span>
          <p>
            Causality is not claimed. SourceMatter forblir invariant; provenance
            chain viser hvordan lesningen ble gjort.
          </p>
        </div>
      </div>

      <button
        type="button"
        className="v25a-explain-open"
        onClick={onOpenResearch}
      >
        Vis full provenance chain →
      </button>
    </section>
  );
}

export default function ResearchExplanation(props) {
  if (props.mode === "v25") {
    return <V25Explanation {...props} />;
  }
  return <V24Explanation {...props} />;
}
